<template>
  <div class="select-wrapper">
    <div class="select" :class="{ open, disabled, error }" v-click-outside="close">
      <!-- Trigger -->
      <button class="trigger" type="button" :disabled="disabled" @click="toggle" @keydown.down.prevent="openList()"
        @keydown.up.prevent="openList()" @keydown.enter.prevent="toggle" :aria-expanded="open ? 'true' : 'false'"
        aria-haspopup="listbox" :aria-invalid="error ? 'true' : 'false'">
        <span class="label" :class="{ placeholder: !current }">
          {{ current ? current[labelKey] : placeholder }}
        </span>

        <!-- caret -->
        <svg class="caret" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="#1f3fb7" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>

      <!-- Dropdown -->
      <div class="panel" v-show="open">
        <ul class="list" role="listbox" :aria-activedescendant="activeId" ref="listEl" @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)" @keydown.enter.prevent="commitActive" @keydown.esc.prevent="close"
          tabindex="0">
          <li v-for="(opt, i) in options" :key="opt[valueKey]" class="item" :id="idFor(i)"
            :class="{ active: i === activeIndex, selected: isSelected(opt) }" role="option" @click="select(opt)"
            @mousemove="activeIndex = i">
            {{ opt[labelKey] }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Error message (like your checkbox error) -->
    <div v-if="error && errorMessage" class="select-error-container">
      <img :src="errorLogo" alt="Select error icon" class="select-error-icon" />
      <span class="select-error">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import errorLogo from "@/assets/error-icon.svg";

export default {
  name: 'BaseSelect',
  props: {
    value: [String, Number, Object, null],
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: 'value' },
    labelKey: { type: String, default: 'description' },
    placeholder: { type: String, default: 'válassz' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
  },
  data() {
    return { open: false, activeIndex: -1, errorLogo };
  },
  computed: {
    current() {
      if (this.value == null) return null;
      if (typeof this.value === 'object') return this.value;
      return this.options.find(o => o[this.valueKey] === this.value) || null;
    },
    activeId() { return this.activeIndex >= 0 ? this.idFor(this.activeIndex) : undefined; }
  },
  methods: {
    idFor(i) { return `${this._uid}-opt-${i}`; },
    toggle() { if (!this.disabled) (this.open ? this.close() : this.openList()); },
    openList() {
      if (this.disabled) return;
      this.open = true;
      // set active to selected or first
      const idx = this.current
        ? this.options.findIndex(o => this.isSelected(o))
        : 0;
      this.activeIndex = idx >= 0 ? idx : 0;
      this.$nextTick(() => this.$refs.listEl && this.$refs.listEl.focus());
    },
    close() { this.open = false; },
    isSelected(opt) {
      return this.current && opt[this.valueKey] === this.current[this.valueKey];
    },
    select(opt) {
      const val = typeof this.value === 'object' ? opt : opt[this.valueKey];
      this.$emit('input', val);
      this.$emit('change', opt);
      this.close();
    },
    move(dir) {
      if (!this.open || !this.options.length) return;
      const max = this.options.length - 1;
      let i = this.activeIndex + dir;
      if (i < 0) i = 0;
      if (i > max) i = max;
      this.activeIndex = i;
      const el = document.getElementById(this.idFor(i));
      el && el.scrollIntoView({ block: 'nearest' });
    },
    commitActive() {
      if (this.activeIndex >= 0) this.select(this.options[this.activeIndex]);
    }
  },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__co__ = e => { if (!(el === e.target || el.contains(e.target))) binding.value(e); };
        document.addEventListener('click', el.__co__);
      },
      unbind(el) { document.removeEventListener('click', el.__co__); delete el.__co__; }
    }
  }
};
</script>

<style scoped>
.select {
  position: relative;
  width: 326px;
  height: 44px;
  /* fixed height like the input */
  border: 1px solid #9E9E9E;
  /* subtle gray */
  border-radius: 12px;
  /* pill-like */
  background: #fff;
  box-sizing: border-box;
}

.select-error-container {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.4;
}

.select-error-icon {
  height: 18px;
  margin-right: 6px;
}

.select.error {
  border-color: #D64040 !important;
  background-color: unset !important;
  border-width: 2px;
}

.select-error {
  color: #D64040;
}

.select.disabled {
  opacity: .6;
  cursor: not-allowed;
}

.trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0 14px;
  cursor: pointer;
  outline: none;
}

.label {
  font-size: 16px;
  color: #111827;
}

.label.placeholder {
  font-weight: 300;
  font-size: 16px;
  line-height: 24px;
  color: #656565;
}

.caret {
  flex: 0 0 auto;
  transform: translateX(2px);
}

.select:focus-within {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, .12);
}

.panel {
  position: absolute;
  left: 8px;
  right: 8px;
  top: calc(100% + 8px);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, .08);
  z-index: 30;
  overflow: hidden;
}

.list {
  list-style: none;
  margin: 0;
  padding: 6px 0;
  max-height: 260px;
  overflow: auto;
  outline: none;
}

.item {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 15px;
  color: #111827;
}

.item:hover,
.item.active {
  background: #f3f4f6;
}

.item.selected {
  font-weight: 600;
}

@media (max-width: 575px) {
  .select-wrapper {
    width: 100%;
  }
}
</style>
